'use client'

import { usePathname } from 'next/navigation'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'

interface Capability {
  icon: React.ElementType
  label: string
  query: string
}

// Context-aware capabilities per page
const DASHBOARD_CAPABILITIES: Capability[] = [
  { icon: Icons.activity, label: "What's the auto-coding rate?", query: "What's the auto-coding rate?" },
  { icon: Icons.helpCircle, label: 'How many invoices need review?', query: 'How many invoices need review?' },
  { icon: Icons.users, label: 'Show me top vendors', query: 'Who are the top vendors?' },
  { icon: Icons.fileText, label: 'Generate a status report PDF', query: 'Generate a PDF report of the status summary' },
  { icon: Icons.brain, label: 'Show financial summary', query: "What's the total value posted to Coupa?" },
  { icon: Icons.lightbulb, label: 'Create a policy', query: 'Help me create a new policy' },
]

const WORKBENCH_CAPABILITIES: Capability[] = [
  { icon: Icons.fileText, label: 'Summarize this invoice', query: 'Summarize this invoice' },
  { icon: Icons.brain, label: 'Explain AI reasoning', query: 'Explain the AI reasoning for this invoice' },
  { icon: Icons.helpCircle, label: "What's the billing string?", query: "What's the billing string for this invoice?" },
  { icon: Icons.zap, label: 'Push to Coupa', query: 'Push this invoice to Coupa' },
  { icon: Icons.info, label: 'Show line details', query: 'Show me the per-line coding details' },
  { icon: Icons.alertTriangle, label: 'Reject this invoice', query: 'Reject this invoice' },
]

const EXPLORER_CAPABILITIES: Capability[] = [
  { icon: Icons.search, label: 'Show needs review invoices', query: 'Show me all invoices that need review' },
  { icon: Icons.users, label: 'Find invoices by vendor', query: 'Show me all invoices from the top vendor' },
  { icon: Icons.fileText, label: 'Generate PDF of auto-coded', query: 'Generate a PDF report of all auto-coded invoices' },
  { icon: Icons.activity, label: "What's the confidence distribution?", query: 'What is the average confidence for auto-coded invoices?' },
  { icon: Icons.helpCircle, label: 'What can you help with?', query: 'What can you help me with?' },
  { icon: Icons.lightbulb, label: 'Show insights', query: 'Show me AI insights' },
]

const DEFAULT_CAPABILITIES: Capability[] = [
  { icon: Icons.helpCircle, label: 'What can you help me with?', query: 'What can you help me with?' },
  { icon: Icons.activity, label: 'Show dashboard KPIs', query: "What's the auto-coding rate?" },
  { icon: Icons.fileText, label: 'Generate a report', query: 'Generate a report' },
  { icon: Icons.brain, label: 'Create a policy', query: 'Help me create a new policy' },
  { icon: Icons.lightbulb, label: 'Show insights', query: 'Show me AI insights' },
  { icon: Icons.info, label: 'Explain this page', query: 'Explain this page to me' },
]

interface CapabilityBubblesProps {
  onSelect: (query: string) => void
}

export function CapabilityBubbles({ onSelect }: CapabilityBubblesProps) {
  const pathname = usePathname()

  // Select capabilities based on current page
  let capabilities: Capability[]
  if (pathname?.startsWith('/workbench/')) {
    capabilities = WORKBENCH_CAPABILITIES
  } else if (pathname === '/invoice-explorer') {
    capabilities = EXPLORER_CAPABILITIES
  } else if (pathname === '/') {
    capabilities = DASHBOARD_CAPABILITIES
  } else {
    capabilities = DEFAULT_CAPABILITIES
  }

  return (
    <div className="flex flex-wrap justify-center gap-2 p-2">
      {capabilities.map((cap, i) => {
        const Icon = cap.icon
        return (
          <motion.button
            key={`${pathname}-${i}`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.25, ease: 'easeOut' }}
            onClick={() => onSelect(cap.query)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-full',
              'bg-white border border-brand-cornflower/20',
              'text-sm text-brand-navy',
              'shadow-sm',
              'transition-all duration-200',
              'hover:bg-brand-cornflower/10 hover:border-brand-cornflower/40 hover:shadow-md',
              'focus:outline-none focus:ring-2 focus:ring-brand-cornflower/50'
            )}
          >
            <Icon className="h-4 w-4 text-brand-cornflower" strokeWidth={1.5} />
            <span className="text-xs sm:text-sm font-medium">{cap.label}</span>
          </motion.button>
        )
      })}
    </div>
  )
}
